/**
 * @file Ledger shapes for a multi-part Signify fetch (SYD-2352): the job document at
 * `signifyFetchJobs/{jobId}`, one part document per input under its `parts` subcollection, and the
 * request and response shapes of the start, retry and cancel callables.
 * Written by the job worker through the admin SDK, read by the progress modal in `react/src/`,
 * which also stamps `insertedAt` — the only client-writable field on either shape.
 * Invariants: counts, progress and ETA are DERIVED from the parts subcollection and never stored
 * on the job, so the two can never disagree; the worker's only progress rule is "skip terminal
 * parts" (`done` and `failed`), which makes queue redelivery, deadline continuation and operator
 * retry one code path; `result` is reachable only on `done`, so a caller cannot read a payload
 * that was never fetched. `expiresAt` drives the TTL policies declared in
 * `firestore.indexes.json` `fieldOverrides` and is a `Timestamp`, the only type TTL acts on;
 * TTL does not cascade, which is why the part carries its own copy of the job's value under a
 * `COLLECTION_GROUP`-scoped policy.
 */

import type { DocId, TimeType } from './System.types';
import type { ProductAndPriceAndAvailabilityResponse, ProductSearchResult } from './SignifyApi.types';

export const SIGNIFY_FETCH_JOBS_COLLECTION = 'signifyFetchJobs';

export function signifyFetchJobPath(jobId: string): string {
  return `${SIGNIFY_FETCH_JOBS_COLLECTION}/${jobId}`;
}

export function signifyFetchJobPartsPath(jobId: string): string {
  return `${SIGNIFY_FETCH_JOBS_COLLECTION}/${jobId}/parts`;
}

/**
 * Hard cap on the inputs one job accepts. Deliberately below `AR_TABLE_MAX_ROWS` (1500, in
 * `types/src/System.Parameters.types.ts`), which bounds an ArTable rather than a Signify fan-out:
 * a part is two Signify requests, so this is the ceiling on the load a single operator can put on
 * the vendor, and it is chosen for that reason rather than to mirror the table limit.
 */
export const SIGNIFY_FETCH_JOB_MAX_INPUTS = 1000;

/**
 * Lifetime of a job document and of every part under it, written to `expiresAt` at creation.
 * Spelled in literal milliseconds because `TIME_MS` in `SignifyApi.types.ts` is module-private
 * and exporting it for one constant would widen that module's surface for nothing.
 */
export const SIGNIFY_FETCH_JOB_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export type SignifyFetchPartState = 'pending' | 'running' | 'ambiguous' | 'done' | 'failed';

/**
 * Fields every part carries in every state. `docId` is the part's document id, fixed at job
 * creation from the operator's entry position and never changed — `code` is a field resolution
 * fills in, never the id. `input` is the operator's text verbatim, a pasted token or a search
 * pick, and `code` is the 12NC it resolved to, null until resolution succeeds. `product` holds a
 * search pick's payload from the moment the job is created, so a picked part is never re-fetched;
 * it is null for a pasted token until resolution finds one. `order` is the operator's position,
 * the only ordering the part table uses. `userId` is denormalized from the job so `firestore.rules`
 * can check ownership without a `get()` on the parent. `expiresAt` copies the job's value because
 * Firestore TTL does not cascade into subcollections. `insertedAt` is written by the CLIENT when
 * the part has been inserted into the target table or entity; null means not inserted, and it is
 * the sole field the rules let a client change.
 */
type SignifyFetchPartBase = {
  docId: DocId;
  userId: string;
  input: string;
  order: number;
  quantity: number;
  code: string | null;
  product: ProductSearchResult | null;
  insertedAt: number | null;
  expiresAt: TimeType;
};

/**
 * One part document at `signifyFetchJobs/{jobId}/parts/{partId}`, discriminated on `state`.
 * `done` narrows `code` to a resolved 12NC and is the only state carrying `result`.
 * `ambiguous` means resolution found several products and the operator must pick one;
 * `failed` means the part has no product payload after resolution — a missing price or
 * availability keeps today's optional semantics and still yields `done`.
 */
export type SignifyFetchPart =
  | (SignifyFetchPartBase & { state: 'pending' })
  | (SignifyFetchPartBase & { state: 'running'; startedAt: number })
  | (SignifyFetchPartBase & { state: 'ambiguous'; candidates: ProductSearchResult[] })
  | (SignifyFetchPartBase & {
      state: 'done';
      code: string;
      completedAt: number;
      fromCache: boolean;
      result: ProductAndPriceAndAvailabilityResponse;
    })
  | (SignifyFetchPartBase & { state: 'failed'; completedAt: number; attempts: number; error: string });

export type SignifyFetchJobPhase = 'queued' | 'running' | 'completed' | 'cancelled';

/**
 * The job document. `inputs` is the operator's order, deduped, verbatim; a part's document id is
 * the entry key assigned from that order at creation and is immutable, so this array and the part
 * ids agree for the life of the job. `targetKey` is an opaque token the client supplies and the
 * server never interprets: it is how a reattaching client tells its own job from one started
 * against a different table. `concurrency.cap` is the live AIMD gate capacity, `initial` the
 * capacity the current dispatch started at and `pausedUntil` the instant a congestion pause lifts.
 * `initial` is written once per dispatch and never mutated, which is what lets the modal keep
 * saying "slowed by Signify" after the pause lifts and the gate is still running at half capacity —
 * a comparison against the live cap alone cannot tell reduced from normal. `lastError` carries the
 * job-level failures that have no per-part home — a
 * broken token chain, missing configuration — and is null on every healthy job. `continuation`
 * counts deadline re-enqueues. `heartbeatAt` is written every five seconds by the dispatch's
 * control poll; the client shows "stalled" once it is more than 120s old. `cancelRequested` is
 * honoured between parts, so in-flight requests always finish.
 */
export type SignifyFetchJob = {
  docId: DocId;
  userId: string;
  customerId: string;
  distributorId: string;
  targetKey: string;
  inputs: string[];
  phase: SignifyFetchJobPhase;
  cancelRequested: boolean;
  concurrency: { cap: number; initial: number; pausedUntil: number | null };
  lastError: string | null;
  heartbeatAt: number;
  continuation: number;
  createdAt: number;
  expiresAt: TimeType;
};

/**
 * `startSignifyFetchJob`. For a search pick the client sets `input` to `product.code` and
 * `product` to the pick; for a pasted token `input` is the token verbatim and `product` is absent.
 * `customerId` and `distributorId` fall back to the server's configured defaults when omitted.
 * Entries are deduped on `input.trim().toUpperCase()` after the length check, so a client sending
 * more than `SIGNIFY_FETCH_JOB_MAX_INPUTS` is rejected rather than silently trimmed.
 */
export type StartSignifyFetchJobRequest = {
  customerId?: string;
  distributorId?: string;
  targetKey: string;
  entries: Array<{ input: string; quantity?: number; product?: ProductSearchResult }>;
};

export type StartSignifyFetchJobResponse = { jobId: string };

/**
 * `retrySignifyFetchJob`. `picks` resolves `ambiguous` parts — each names a candidate already on
 * that part — and `failed` parts are reset to `pending` whether or not picks are sent.
 * `partIds` narrows that reset to the parts it names, which is what one row's Retry sends; absent,
 * every `failed` part is reset, which is what the footer button sends. A named part that is not
 * `failed` is skipped rather than refused, because the operator may have pressed twice.
 * `reset` counts the parts returned to `pending`; it can be zero, and the job is enqueued anyway
 * so a dispatch can reclaim parts orphaned in `running`.
 */
export type RetrySignifyFetchJobRequest = {
  jobId: string;
  picks?: Array<{ partId: string; code: string }>;
  partIds?: string[];
};

export type RetrySignifyFetchJobResponse = { jobId: string; reset: number };

/** `cancelSignifyFetchJob`. In-flight parts finish; pending parts are left untouched. */
export type CancelSignifyFetchJobRequest = { jobId: string };

export type CancelSignifyFetchJobResponse = { jobId: string };
